import { resolveTimeZone, zoneLabel } from '@/lib/displayTimeZone';
import type { DisplayTimeZone, RelativePreset } from './types';
import {
  localMidnightUtc,
  supportedTimeZones,
  zonedParts,
  zonedWallTimeToUtc,
} from '@kirin/api-client/helpers/time-zone';

export const RELATIVE_PRESETS: {
  key: RelativePreset;
  label: string;
  getRange: (tz?: string) => { from: Date; to: Date };
}[] = [
  {
    key: '30min',
    label: 'Last 30 minutes',
    getRange: () => rangeMs(30 * 60 * 1000),
  },
  { key: '1h', label: 'Last 1 hour', getRange: () => rangeMs(60 * 60 * 1000) },
  {
    key: '6h',
    label: 'Last 6 hours',
    getRange: () => rangeMs(6 * 60 * 60 * 1000),
  },
  {
    key: '12h',
    label: 'Last 12 hours',
    getRange: () => rangeMs(12 * 60 * 60 * 1000),
  },
  {
    key: '1d',
    label: 'Last 1 day',
    getRange: () => rangeMs(24 * 60 * 60 * 1000),
  },
  {
    key: '7d',
    label: 'Last 7 days',
    getRange: (tz?: string) => rangeDays(7, tz),
  },
  {
    key: '14d',
    label: 'Last 14 days',
    getRange: (tz?: string) => rangeDays(14, tz),
  },
  {
    key: '30d',
    label: 'Last 30 days',
    getRange: (tz?: string) => rangeDays(30, tz),
  },
  {
    key: '3m',
    label: 'Last 3 months',
    getRange: (tz?: string) => rangeMonths(3, tz),
  },
  {
    key: '6m',
    label: 'Last 6 months',
    getRange: (tz?: string) => rangeMonths(6, tz),
  },
  {
    key: '12m',
    label: 'Last 12 months',
    getRange: (tz?: string) => rangeMonths(12, tz),
  },
];

export function rangeMs(ms: number): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - ms);
  return { from, to };
}

// Computes a calendar-day-aligned range: from = local midnight (days-1) days
// ago in the given timezone, to = now. This ensures day-level bucket labels
// always include today (in the user's timezone) as the last bucket.
export function rangeDays(days: number, tz?: string): { from: Date; to: Date } {
  const to = new Date();
  const from = localMidnightUtc(resolveTimeZone(tz), to, days - 1);
  return { from, to };
}

/**
 * From the same wall-clock time `months` calendar months before `to`, in
 * `timeZone`, to `to`. The day is clamped to the target month's last (May 31
 * minus 3 months is the end of February), and a DST change between them
 * keeps the wall clock, not a 24 h multiple.
 */
export function calculateMonthRange(
  to: Date,
  months: number,
  timeZone: string
): { from: Date; to: Date } {
  const p = zonedParts(timeZone, to);
  // Months counted from year 0, so Date.UTC can't roll a short month over.
  const target = p.year * 12 + (p.month - 1) - months;
  const year = Math.floor(target / 12);
  const month = (target % 12) + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const from = zonedWallTimeToUtc(
    timeZone,
    year,
    month,
    Math.min(p.day, lastDay),
    p.hour,
    p.minute
  );
  from.setTime(from.getTime() + p.second * 1000 + to.getMilliseconds());
  return { from, to };
}

export function rangeMonths(
  months: number,
  tz?: string
): { from: Date; to: Date } {
  return calculateMonthRange(new Date(), months, resolveTimeZone(tz));
}

/**
 * The selector's zones: the special 'browser' option, 'UTC', then every zone
 * the app lists, each labelled with its offset now, as Settings does
 * (`Etc/GMT+5` reads GMT-5, not +5). Built when the popover opens, not at
 * import: the first build makes ~445 formatters (~20ms).
 */
export function timeZoneOptions(): { value: DisplayTimeZone; label: string }[] {
  const now = new Date();
  return [
    { value: 'browser', label: 'Browser (Local)' },
    { value: 'UTC', label: 'UTC' },
    ...supportedTimeZones()
      .filter(tz => tz !== 'UTC')
      .map(tz => ({ value: tz, label: zoneLabel(tz, now) })),
  ];
}
